import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import { useNavigate } from 'react-router-dom';

const ReinforcementLearning = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'Reinforcement Learning Services - RL Models & Agents | OctalChip',
    description: 'Professional reinforcement learning development services. We build intelligent RL agents and models for autonomous decision-making, game AI, robotics, and optimization problems.',
    keywords: 'reinforcement learning, RL development, RL agents, Q-learning, deep RL, policy gradient, autonomous systems, game AI, robotics',
    canonical: 'https://www.octalchip.com/services/ai-ml-services/reinforcement-learning',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Reinforcement Learning Development Services",
      "description": "Intelligent RL agents and models for autonomous decision-making and optimization",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const features = [
    {
      title: 'RL Agent Development',
      description: 'Custom reinforcement learning agents designed to learn optimal policies through interaction with environments',
      icon: 'Brain'
    },
    {
      title: 'Deep Q-Networks (DQN)',
      description: 'Advanced deep Q-learning networks for complex decision-making in high-dimensional state spaces',
      icon: 'Network'
    },
    {
      title: 'Policy Gradient Methods',
      description: 'Policy optimization using REINFORCE, Actor-Critic, and PPO algorithms for continuous control',
      icon: 'TrendingUp'
    },
    {
      title: 'Multi-Agent Systems',
      description: 'Collaborative and competitive multi-agent RL systems for complex interactive environments',
      icon: 'Users'
    },
    {
      title: 'Environment Simulation',
      description: 'Custom simulation environments and integration with OpenAI Gym, Unity ML-Agents, and other platforms',
      icon: 'Monitor'
    },
    {
      title: 'Reward Engineering',
      description: 'Expert design of reward functions and shaping techniques to guide agent learning effectively',
      icon: 'Star'
    }
  ];

  const technologies = [
    { name: 'PyTorch', category: 'Framework', description: 'Deep learning framework for RL implementations' },
    { name: 'TensorFlow', category: 'Framework', description: 'Google\'s framework with RL libraries' },
    { name: 'OpenAI Gym', category: 'Platform', description: 'Standard toolkit for RL environments' },
    { name: 'Stable Baselines3', category: 'Library', description: 'High-quality RL algorithm implementations' },
    { name: 'Ray RLlib', category: 'Library', description: 'Scalable RL library for distributed training' },
    { name: 'Unity ML-Agents', category: 'Platform', description: 'Unity-based RL environment and training' },
    { name: 'TensorFlow Agents', category: 'Library', description: 'TF-Agents for RL research and production' },
    { name: 'Python', category: 'Language', description: 'Primary language for RL development' }
  ];

  const useCases = [
    {
      title: 'Game AI & Strategy',
      description: 'Develop intelligent game-playing agents for chess, Go, video games, and strategic decision-making',
      icon: 'Gamepad2'
    },
    {
      title: 'Robotics & Control',
      description: 'Autonomous robot control, manipulation, navigation, and continuous control systems',
      icon: 'Cpu'
    },
    {
      title: 'Autonomous Vehicles',
      description: 'Self-driving car decision-making, path planning, and adaptive driving behaviors',
      icon: 'Car'
    },
    {
      title: 'Resource Optimization',
      description: 'Dynamic resource allocation, scheduling, and optimization in complex systems',
      icon: 'Settings'
    },
    {
      title: 'Trading & Finance',
      description: 'Algorithmic trading strategies, portfolio optimization, and market making agents',
      icon: 'TrendingUp'
    },
    {
      title: 'Recommendation Systems',
      description: 'Interactive recommendation agents that learn from user feedback and adapt over time',
      icon: 'Star'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Enhanced Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-[#00d690]/10 via-white to-[#00d690]/5 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #00d690 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-2xl mb-8 shadow-lg transform hover:scale-110 transition-transform duration-300">
              <Icon name="Network" size={48} className="text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-800 mb-6 leading-tight">
              Reinforcement Learning
              <span className="block text-[#00d690] mt-2">Solutions</span>
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mx-auto mb-8"></div>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
              Build intelligent reinforcement learning agents that learn optimal strategies through interaction. 
              We develop RL solutions for autonomous systems, game AI, robotics, and complex optimization problems.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                variant="primary" 
                onClick={() => navigate('/services#share-your-requirements')}
                className="px-10 py-4 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                Start Your Project
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/ai-ml-services')}
                className="px-10 py-4 text-lg border-2"
              >
                View All AI Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">10+</div>
              <div className="text-gray-600 font-medium">RL Projects</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">6-14</div>
              <div className="text-gray-600 font-medium">Weeks Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">90%+</div>
              <div className="text-gray-600 font-medium">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">24/7</div>
              <div className="text-gray-600 font-medium">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Key Features</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive features designed to deliver exceptional reinforcement learning solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-xl flex items-center justify-center mb-6 group-hover:from-[#00d690] group-hover:to-[#00b876] transition-all duration-300">
                  <Icon name={feature.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Technologies We Master</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We work with the latest and most powerful RL technologies to build intelligent agents
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {technologies.map((tech, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-gray-800 group-hover:text-[#00d690] transition-colors">
                    {tech.name}
                  </span>
                  <span className="text-xs font-medium text-[#00d690] bg-[#00d690]/10 px-2 py-1 rounded">
                    {tech.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-gradient-to-br from-[#00d690]/5 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">What We Build</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From game AI to autonomous systems, we deliver RL solutions for diverse applications
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group border-l-4 border-[#00d690]"
              >
                <div className="w-14 h-14 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#00d690] transition-all duration-300">
                  <Icon name={useCase.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">
                  {useCase.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Development Process</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures quality, transparency, and timely delivery
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="hidden md:block absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00d690] to-[#00b876]"></div>
              
              <div className="space-y-12">
                {[
                  {
                    step: '01',
                    title: 'Problem Definition & Environment Setup',
                    description: 'We analyze your problem domain, define the RL task, set up the environment, and establish state-action spaces and reward structures',
                    icon: 'Search'
                  },
                  {
                    step: '02',
                    title: 'Reward Function Design',
                    description: 'We design effective reward functions that guide agent learning, implement reward shaping, and balance exploration vs exploitation',
                    icon: 'Star'
                  },
                  {
                    step: '03',
                    title: 'Algorithm Selection & Architecture',
                    description: 'We select appropriate RL algorithms (DQN, PPO, A3C, etc.), design neural network architectures, and configure hyperparameters',
                    icon: 'Code'
                  },
                  {
                    step: '04',
                    title: 'Training & Optimization',
                    description: 'We train RL agents using simulation environments, optimize hyperparameters, implement experience replay, and monitor learning progress',
                    icon: 'Settings'
                  },
                  {
                    step: '05',
                    title: 'Evaluation & Testing',
                    description: 'We evaluate agent performance, test in diverse scenarios, measure convergence, and validate robustness and generalization',
                    icon: 'CheckCircle'
                  },
                  {
                    step: '06',
                    title: 'Deployment & Continuous Learning',
                    description: 'We deploy trained agents to production, implement online learning capabilities, and continuously improve performance through feedback',
                    icon: 'Rocket'
                  }
                ].map((step, index) => (
                  <div key={index} className="relative flex flex-col md:flex-row items-start gap-6">
                    {/* Step Number */}
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
                      {step.step}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4">
                          <Icon name={step.icon} size={24} className="text-[#00d690]" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Our Reinforcement Learning Services?</h2>
              <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Expert RL engineers with deep expertise in modern RL algorithms and frameworks',
                'Custom RL solutions tailored to your specific problem domain and requirements',
                'End-to-end development from environment design to production deployment',
                'Advanced algorithms including DQN, PPO, A3C, and custom policy gradients',
                'Efficient training pipelines with distributed computing and GPU acceleration',
                'Robust evaluation and testing methodologies for reliable agent performance',
                'Seamless integration with existing systems and real-world environments',
                'Ongoing support, monitoring, and continuous improvement of RL agents'
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#00d690] rounded-full flex items-center justify-center">
                    <Icon name="Check" size={20} className="text-white" />
                  </div>
                  <p className="text-lg text-gray-200">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Ready to Build Your Reinforcement Learning Solution?
            </h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Let's discuss your project requirements and create a solution that drives your business forward. 
              Get a free consultation and quote today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/services#share-your-requirements')}
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project
              </button>
              <button
                onClick={() => navigate('/ai-ml-services')}
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-4 px-8 rounded-lg transition-all duration-300"
              >
                View All AI Services
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReinforcementLearning;

